<script setup lang="ts">
import { X } from 'lucide-vue-next'
import { computed, nextTick, shallowRef, useTemplateRef, watch } from 'vue'
import AppErrorState from '@/components/app-error-state'
import SummaryContentCard from '@/components/summary-content-card'
import { useScreenSize } from '@/composables/useScreenSize'
import {
  distributeItemsByIndex,
  distributeSummaryContentItems,
  resolveSummaryContentColumnCount,
} from '@/utils/profile/content-grid'
import type { Emits, Props } from './type'

defineOptions({
  name: 'SearchBeforePanel',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingHotWords: false,
  error: null,
})

const emit = defineEmits<Emits>()
const { screenWidth } = useScreenSize()
const selectedRecommendationId = shallowRef<string | null>(null)
const hotWordsListRef = useTemplateRef<HTMLElement>('hotWordsList')
const HOT_WORDS_DEFAULT_SKELETON_ROW_COUNT = 2
const hotWordsSkeletonRowCount = shallowRef(HOT_WORDS_DEFAULT_SKELETON_ROW_COUNT)
const recommendationColumnCount = computed(() =>
  resolveSummaryContentColumnCount(screenWidth.value),
)
const activeRecommendation = computed(() => {
  if (props.recommendations.length === 0) {
    return null
  }

  return (
    props.recommendations.find(
      (section) => section.category.id === selectedRecommendationId.value,
    ) ??
    props.recommendations[0] ??
    null
  )
})
const recommendationColumns = computed(() =>
  distributeSummaryContentItems(
    activeRecommendation.value?.items ?? [],
    recommendationColumnCount.value,
  ),
)
const skeletonItems = computed(() =>
  Array.from({ length: Math.max(10, recommendationColumnCount.value * 2) }, (_, index) => index),
)
const skeletonColumns = computed(() =>
  distributeItemsByIndex(skeletonItems.value, recommendationColumnCount.value),
)
const showHotWordsLoading = computed(() => props.loadingHotWords)
const showHotWordsSection = computed(() => showHotWordsLoading.value || props.hotWords.length > 0)
const hotWordsContentKey = computed(
  () => props.hotWords.map((word) => word.id).join('|') || 'empty',
)

function resolveChipRowCount(element: HTMLElement) {
  const rowTops = new Set<number>()

  for (const child of element.children) {
    if (!(child instanceof HTMLElement)) {
      continue
    }

    rowTops.add(Math.round(child.offsetTop))
  }

  return Math.max(1, rowTops.size)
}

function rememberHotWordsRowCount() {
  const hotWordsList = hotWordsListRef.value

  if (!hotWordsList || hotWordsList.children.length === 0) {
    return
  }

  hotWordsSkeletonRowCount.value = resolveChipRowCount(hotWordsList)
}

function handleRefreshHotWords() {
  rememberHotWordsRowCount()
  emit('refreshHotWords')
}

watch(showHotWordsLoading, (isLoading, wasLoading) => {
  if (isLoading && !wasLoading) {
    rememberHotWordsRowCount()
    return
  }

  if (!isLoading) {
    nextTick(rememberHotWordsRowCount)
  }
})

watch(
  [hotWordsContentKey, screenWidth],
  () => {
    if (showHotWordsLoading.value) {
      return
    }

    nextTick(rememberHotWordsRowCount)
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => props.recommendations.map((section) => section.category.id).join('|'),
  () => {
    if (
      selectedRecommendationId.value &&
      props.recommendations.some(
        (section) => section.category.id === selectedRecommendationId.value,
      )
    ) {
      return
    }

    selectedRecommendationId.value = props.recommendations[0]?.category.id ?? null
  },
  { immediate: true },
)
</script>

<template>
  <section class="search-before" aria-label="搜索首页">
    <div v-if="recentSearches.length" class="search-before__section search-before__section--narrow">
      <div class="search-before__section-head">
        <div class="search-before__title">
          <h2>最近搜索</h2>
        </div>
        <button type="button" class="search-before__text-button" @click="emit('clearRecent')">
          清空
        </button>
      </div>

      <div class="search-before__chips">
        <span v-for="keyword in recentSearches" :key="keyword" class="search-before__chip-wrap">
          <button
            type="button"
            class="search-before__chip"
            :title="keyword"
            @click="emit('search', keyword)"
          >
            <span class="search-before__chip-label">{{ keyword }}</span>
          </button>
          <button
            type="button"
            class="search-before__chip-remove"
            :aria-label="`删除 ${keyword}`"
            @click="emit('removeRecent', keyword)"
          >
            <X :size="13" />
          </button>
        </span>
      </div>
    </div>

    <div v-if="showHotWordsSection" class="search-before__section search-before__section--narrow">
      <div class="search-before__section-head">
        <div class="search-before__title">
          <h2>猜你想搜</h2>
        </div>
        <button
          type="button"
          class="search-before__text-button"
          :disabled="showHotWordsLoading"
          @click="handleRefreshHotWords"
        >
          换一批
        </button>
      </div>

      <Transition name="search-before-hotwords" mode="out-in">
        <div
          v-if="showHotWordsLoading"
          key="hotwords-loading"
          class="search-before__chips search-before__chips--hot search-before__chips--loading"
          role="status"
          aria-label="猜你想搜加载中"
        >
          <span
            v-for="rowIndex in hotWordsSkeletonRowCount"
            :key="`hotwords-skeleton-row-${rowIndex}`"
            class="search-before__chip-skeleton-row"
            aria-hidden="true"
          >
            <span class="search-before__chip-skeleton is-medium" />
            <span class="search-before__chip-skeleton is-wide" />
            <span class="search-before__chip-skeleton is-short" />
          </span>
        </div>

        <div
          v-else-if="hotWords.length"
          :key="`hotwords-${hotWordsContentKey}`"
          ref="hotWordsList"
          class="search-before__chips search-before__chips--hot"
        >
          <button
            v-for="word in hotWords"
            :key="word.id"
            type="button"
            class="search-before__chip is-hot"
            :title="word.label"
            @click="emit('search', word.keyword)"
          >
            <span class="search-before__chip-label">{{ word.label }}</span>
          </button>
        </div>
      </Transition>
    </div>

    <div class="search-before__section">
      <div class="search-before__section-head">
        <div
          v-if="recommendations.length"
          class="search-before__category-tabs"
          role="tablist"
          aria-label="推荐类别"
        >
          <button
            v-for="section in recommendations"
            :key="section.category.id"
            type="button"
            class="search-before__category-tab"
            :class="{ 'is-active': activeRecommendation?.category.id === section.category.id }"
            role="tab"
            :aria-selected="activeRecommendation?.category.id === section.category.id"
            @click="selectedRecommendationId = section.category.id"
          >
            {{ section.category.name }}
          </button>
        </div>
      </div>

      <AppErrorState
        v-if="error && !recommendations.length"
        :message="error"
        variant="inline"
        @retry="emit('retry')"
      />

      <template v-else-if="activeRecommendation && activeRecommendation.items.length">
        <section class="search-before__recommendation">
          <div
            class="search-before__masonry"
            :style="{ '--search-before-columns': String(recommendationColumnCount) }"
          >
            <div
              v-for="(column, columnIndex) in recommendationColumns"
              :key="`recommendation-column-${columnIndex}`"
              class="search-before__column"
            >
              <SummaryContentCard
                v-for="(item, index) in column"
                :key="item.id"
                :item="item"
                :priority="columnIndex < 2 && index < 2"
                @click="emit('workClick', item)"
                @author-click="emit('authorClick', item)"
              />
            </div>
          </div>
        </section>
      </template>

      <div
        v-else-if="loading"
        class="search-before__masonry"
        :style="{ '--search-before-columns': String(recommendationColumnCount) }"
        aria-label="推荐内容加载中"
      >
        <div
          v-for="(column, columnIndex) in skeletonColumns"
          :key="`recommendation-skeleton-column-${columnIndex}`"
          class="search-before__column"
        >
          <div v-for="item in column" :key="item" class="search-before__card-skeleton">
            <div
              class="search-before__cover-skeleton"
              :class="{ 'is-short': item % 3 === 1, 'is-square': item % 5 === 0 }"
            />
            <div class="search-before__line-skeleton" />
            <div class="search-before__line-skeleton is-short" />
          </div>
        </div>
      </div>

      <p v-else class="search-before__state">暂无推荐内容</p>
    </div>
  </section>
</template>

<style scoped>
.search-before {
  display: flex;
  flex-direction: column;
  gap: 28px;
  --search-before-chip-height: 38px;
  --search-before-chip-max-width: 280px;
  --search-before-chip-remove-width: 32px;
}

.search-before__section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
}

.search-before__section--narrow {
  width: min(100%, 640px);
  margin: 0 auto;
  padding: 0 16px;
}

.search-before__section-head {
  display: flex;
  width: min(100%, 640px);
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 auto;
  padding: 0 16px;
}

.search-before__section--narrow .search-before__section-head {
  width: 100%;
  padding: 0 4px;
}

.search-before__title {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  color: var(--app-text-main);
}

.search-before__title h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 21px;
}

.search-before__text-button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border: 0;
  appearance: none;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  outline: none;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-before__text-button:hover,
.search-before__text-button:focus-visible {
  background: var(--app-state-layer-hover);
  color: var(--app-text-main);
}

.search-before__text-button:disabled {
  background: transparent;
  color: var(--app-text-muted);
  cursor: default;
  opacity: 0.58;
}

.search-before__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-before__chips--hot {
  min-height: var(--search-before-chip-height);
}

.search-before__chips--loading {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
}

.search-before__chip-skeleton-row {
  display: flex;
  min-width: 0;
  gap: 8px;
  overflow: hidden;
}

.search-before-hotwords-enter-active,
.search-before-hotwords-leave-active {
  transition:
    opacity 0.18s ease,
    filter 0.18s ease,
    transform 0.22s var(--app-interaction-ease);
}

.search-before-hotwords-enter-from {
  opacity: 0;
  filter: blur(2px);
  transform: translateY(5px);
}

.search-before-hotwords-leave-to {
  opacity: 0;
  filter: blur(1px);
  transform: translateY(-4px);
}

.search-before__chip-wrap {
  display: inline-flex;
  height: var(--search-before-chip-height);
  max-width: min(
    100%,
    calc(var(--search-before-chip-max-width) + var(--search-before-chip-remove-width))
  );
  align-items: center;
  overflow: hidden;
  border: 1px solid var(--app-border-subtle);
  border-radius: 12px;
  background: transparent;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    border-color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-before__chip-wrap:hover,
.search-before__chip-wrap:focus-within {
  background: var(--app-state-layer-hover);
}

.search-before__chip-wrap:active {
  background: var(--app-state-layer-pressed);
}

.search-before__chip,
.search-before__chip-remove {
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-main);
  cursor: pointer;
  outline: none;
}

.search-before__chip {
  display: inline-flex;
  height: var(--search-before-chip-height);
  max-width: min(var(--search-before-chip-max-width), 100%);
  min-width: 0;
  min-height: var(--search-before-chip-height);
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  overflow: hidden;
  border: 1px solid var(--app-border-subtle);
  border-radius: 12px;
  background: transparent;
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    border-color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-before__chip-wrap .search-before__chip {
  height: 100%;
  flex: 1 1 auto;
  align-self: stretch;
  max-width: none;
  min-height: 0;
  border: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.search-before__chip-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-before__chip.is-hot {
  background: transparent;
}

.search-before__chip:hover,
.search-before__chip:focus-visible {
  background: var(--app-state-layer-hover);
}

.search-before__chip-wrap .search-before__chip:hover,
.search-before__chip-wrap .search-before__chip:focus-visible {
  background: transparent;
}

.search-before__chip:active {
  background: var(--app-state-layer-pressed);
}

.search-before__chip-wrap .search-before__chip:active {
  background: transparent;
}

.search-before__chip-remove:hover,
.search-before__chip-remove:focus-visible {
  color: var(--app-text-main);
}

.search-before__chip-remove:active {
  color: var(--app-text-main);
}

.search-before__chip-remove {
  display: inline-flex;
  width: var(--search-before-chip-remove-width);
  height: var(--search-before-chip-height);
  flex: 0 0 var(--search-before-chip-remove-width);
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  transition: color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-before__chip-wrap .search-before__chip-remove {
  height: 100%;
}

.search-before__category-tabs {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 20px;
  overflow-x: auto;
  scrollbar-width: none;
}

.search-before__category-tabs::-webkit-scrollbar {
  display: none;
}

.search-before__category-tab {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 15px;
  font-weight: 400;
  line-height: 21px;
  outline: none;
  white-space: nowrap;
  transition: color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-before__category-tab:hover,
.search-before__category-tab:focus-visible,
.search-before__category-tab.is-active {
  color: var(--app-text-main);
}

.search-before__category-tab.is-active {
  font-weight: 600;
}

.search-before__recommendation {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.search-before__recommendation + .search-before__recommendation {
  margin-top: 18px;
}

.search-before__masonry {
  display: grid;
  width: 100%;
  max-width: 1440px;
  grid-template-columns: repeat(var(--search-before-columns), minmax(0, 1fr));
  margin: 0 auto;
}

.search-before__column {
  min-width: 0;
}

.search-before__state {
  margin: 0;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 22px;
}

.search-before__chip-skeleton,
.search-before__cover-skeleton,
.search-before__line-skeleton {
  position: relative;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.search-before__chip-skeleton::after,
.search-before__cover-skeleton::after,
.search-before__line-skeleton::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, var(--app-skeleton-shimmer), transparent);
  content: '';
  transform: translateX(-100%);
  animation: search-before-shimmer 1.35s ease-in-out infinite;
}

.search-before__chip-skeleton {
  width: min(var(--search-before-chip-skeleton-width, 96px), calc((100% - 16px) / 3));
  height: var(--search-before-chip-height);
  flex: 0 1 var(--search-before-chip-skeleton-width, 96px);
  border-radius: 12px;
}

.search-before__chip-skeleton.is-short {
  --search-before-chip-skeleton-width: 82px;
}

.search-before__chip-skeleton.is-medium {
  --search-before-chip-skeleton-width: 96px;
}

.search-before__chip-skeleton.is-wide {
  --search-before-chip-skeleton-width: 118px;
}

.search-before__card-skeleton {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 12px 16px;
}

.search-before__cover-skeleton {
  width: 100%;
  aspect-ratio: 293 / 468.8;
  border-radius: 12px;
}

.search-before__cover-skeleton.is-short {
  aspect-ratio: 220 / 293.3333;
}

.search-before__cover-skeleton.is-square {
  aspect-ratio: 1;
}

.search-before__line-skeleton {
  width: 92%;
  height: 14px;
  border-radius: 7px;
}

.search-before__line-skeleton.is-short {
  width: 48%;
}

@keyframes search-before-shimmer {
  to {
    transform: translateX(100%);
  }
}

@media (max-width: 640px) {
  .search-before {
    gap: 24px;
  }

  .search-before__section--narrow,
  .search-before__section-head {
    padding-inline: 10px;
  }

  .search-before__section--narrow .search-before__section-head {
    padding-inline: 4px;
  }

  .search-before__masonry {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .search-before__card-skeleton {
    padding: 7px 5px 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-before-hotwords-enter-active,
  .search-before-hotwords-leave-active {
    transition: none;
  }

  .search-before-hotwords-enter-from,
  .search-before-hotwords-leave-to {
    filter: none;
    transform: none;
  }

  .search-before__chip-skeleton::after,
  .search-before__cover-skeleton::after,
  .search-before__line-skeleton::after {
    animation: none;
  }
}
</style>
